import React, { useEffect, useRef, useState } from "react";
import Cookies from "js-cookie";
import api from "../../../../../chat/api";
import {
  CheckboxCheckedIcon,
  CheckboxUncheckedIcon,
  FolderIcon,
} from "./draftIcons";

// Selection uids — namespaced so KB and project items never collide.
const kbDocUid = (folderId, id) => `kb-doc-${folderId}-${id}`;
const kbFileUid = (id) => `kb-file-${id}`;
const rfpDocUid = (id) => `rfp-doc-${id}`;

const Chevron = ({ up }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 20 20"
    fill="none"
    className={`flex-shrink-0 transition-transform ${up ? "rotate-180" : ""}`}
  >
    <path
      d="M5 7.5L10 12.5L15 7.5"
      stroke="#9CA3AF"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Presentational checkbox; click is handled by the containing row to avoid
// nested-button double-toggle. Indeterminate also shows the checked icon.
const Checkbox = ({ checked, indeterminate }) => (
  <span className="flex-shrink-0 flex items-center justify-center">
    {checked || indeterminate ? <CheckboxCheckedIcon /> : <CheckboxUncheckedIcon />}
  </span>
);

// One collapsible parent-checkbox group (KB or Project). Hoisted to module
// scope so toggling selection doesn't remount the children subtree.
const Group = ({
  label,
  open,
  onToggleOpen,
  allOn,
  someOn,
  selectedCount,
  onToggleAll,
  children,
  showDivider,
}) => (
  <div className={`py-1.5 ${showDivider ? "border-b border-[#DBDBDB]" : ""}`}>
    <div
      className={`flex items-center justify-between px-1 py-2 ${
        open ? " border-[#DBDBDB]" : ""
      }`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <button
          type="button"
          onClick={onToggleAll}
          aria-label={allOn ? "Deselect all" : "Select all"}
        >
          <Checkbox checked={allOn} indeterminate={someOn} />
        </button>
        <button
          type="button"
          onClick={onToggleOpen}
          className="font-outfit text-[14px] font-medium text-[#000000B3] truncate"
        >
          {label}
        </button>
      </div>
      <div className="flex items-center gap-3 flex-shrink-0">
        {selectedCount > 0 && (
          <span className="px-2 py-1 rounded-lg bg-[#F2F2F2] text-[#52525B] font-outfit text-[11px] whitespace-nowrap">
            {selectedCount} Selected
          </span>
        )}
        <button
          type="button"
          onClick={onToggleOpen}
          aria-label={open ? "Collapse" : "Expand"}
        >
          <Chevron up={open} />
        </button>
      </div>
    </div>
    {open && (
      <div className="pl-1.5 pr-2.5 pt-1 pb-3 max-h-[180px] overflow-y-auto custom-scrollbar">
        {children}
      </div>
    )}
  </div>
);

// Two-group knowledge-source picker (Knowledge Base + current Project files).
// Reports the chosen scope via onChange as
// { kb_folder_ids, kb_file_keys, rfp_folder_ids, rfp_file_keys } — all empty
// means "use everything". Mirrors GenerateSectionModal's KB selection logic and
// adds the current project's documents as a second collapsible group.
const KnowledgeSourceScope = ({
  isOpen,
  folderId,
  orgId,
  initialKbScope = null,
  initialRfpScope = null,
  onChange,
  // When restoring for a regenerate, an empty KB scope means the ENTIRE KB was
  // used (that's how a whole-KB pick is stored), so pre-select all KB items
  // rather than showing nothing checked.
  selectAllKbWhenEmpty = false,
}) => {
  const [kbFolders, setKbFolders] = useState([]);
  const [kbFiles, setKbFiles] = useState([]);
  const [rfpDocs, setRfpDocs] = useState([]);
  const [rfpFolderId, setRfpFolderId] = useState(null);
  const [checked, setChecked] = useState({}); // uid -> bool
  const [expanded, setExpanded] = useState({}); // group/folder uid -> bool
  const [kbLoading, setKbLoading] = useState(false);
  const [rfpLoading, setRfpLoading] = useState(false);
  const [kbError, setKbError] = useState(null);
  const [rfpError, setRfpError] = useState(null);
  // True from open until both sources have loaded AND the initial selection has
  // been seeded — lets us show one clean loader instead of flashing empty
  // checkboxes that then flip to checked once the fetch resolves.
  const [initializing, setInitializing] = useState(true);

  // Keep onChange in a ref so reporting selection doesn't depend on the parent
  // passing a stable callback (avoids effect loops).
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  });

  // Load both sources each time the host modal opens, seeding any pre-selection.
  useEffect(() => {
    if (!isOpen) return undefined;
    setChecked({});
    setExpanded({});
    let cancelled = false;

    const organization_id = orgId || Cookies.get("selected_org_id");
    const scopeFolderIds = new Set(
      (initialKbScope?.folders || []).map((f) => String(f.id))
    );
    const scopeFileKeys = new Set(
      (initialKbScope?.files || []).map((f) => f.key).filter(Boolean)
    );
    const rfpScopeFolderIds = new Set(
      (initialRfpScope?.folders || []).map((f) => String(f.id))
    );
    const rfpScopeFileKeys = new Set(
      (initialRfpScope?.files || []).map((f) => f.key).filter(Boolean)
    );

    const fetchKb = async () => {
      setKbLoading(true);
      setKbError(null);
      try {
        const params = {};
        if (organization_id) params.organization_id = organization_id;
        const res = await api.get("/api/v1/rfp/knowledge-base/documents/", {
          params,
        });
        if (cancelled) return;
        const folders = Array.isArray(res.data?.folders) ? res.data.folders : [];
        const files = Array.isArray(res.data?.results) ? res.data.results : [];
        setKbFolders(folders);
        setKbFiles(files);
        if (scopeFolderIds.size || scopeFileKeys.size) {
          // Restore the saved selection but keep the groups collapsed — the
          // "N Selected" badges convey what's picked without unfurling the whole
          // folder tree (which would make the modal tall on open).
          setChecked((prev) => {
            const next = { ...prev };
            folders.forEach((folder) => {
              const docs = folder.documents || [];
              const wholeFolder = scopeFolderIds.has(String(folder.id));
              docs.forEach((d) => {
                if (wholeFolder || (d.key && scopeFileKeys.has(d.key))) {
                  next[kbDocUid(folder.id, d.id)] = true;
                }
              });
            });
            files.forEach((f) => {
              if (f.key && scopeFileKeys.has(f.key)) next[kbFileUid(f.id)] = true;
            });
            return next;
          });
        } else if (selectAllKbWhenEmpty && initialKbScope?.whole !== false) {
          // Empty KB scope is ambiguous, so we lean on the stored `whole` marker:
          //  - whole === true  ⇒ the user picked the entire KB → restore it fully.
          //  - whole === undefined (legacy node, marker not yet stamped) ⇒ assume
          //    whole KB, matching the previous behaviour.
          //  - whole === false ⇒ the user deliberately scoped KB out (e.g. a
          //    project-only pick) → leave KB unchecked (handled by skipping this
          //    branch), so regenerate mirrors exactly what was selected.
          // Restoring it fully also lets the whole-KB normalisation re-apply on
          // submit.
          setChecked((prev) => {
            const next = { ...prev };
            folders.forEach((folder) => {
              (folder.documents || []).forEach((d) => {
                next[kbDocUid(folder.id, d.id)] = true;
              });
            });
            files.forEach((f) => {
              if (f.id != null) next[kbFileUid(f.id)] = true;
            });
            return next;
          });
        }
      } catch (e) {
        if (!cancelled) setKbError("Failed to load knowledge base.");
      } finally {
        if (!cancelled) setKbLoading(false);
      }
    };

    const fetchRfp = async () => {
      if (!folderId) {
        setRfpDocs([]);
        setRfpFolderId(null);
        return;
      }
      setRfpLoading(true);
      setRfpError(null);
      try {
        const url = organization_id
          ? `api/v1/rfp/folders/${folderId}/files/?organization_id=${encodeURIComponent(
              organization_id
            )}`
          : `api/v1/rfp/folders/${folderId}/files/`;
        const res = await api.get(url);
        if (cancelled) return;
        const allDocs = Array.isArray(res.data?.documents) ? res.data.documents : [];
        // Only keep docs the backend can actually retrieve against — matches
        // _resolve_rfp_scope_keys (status ingested/ready). Selecting anything else
        // would enable the button but be silently dropped server-side.
        const docs = allDocs.filter((d) =>
          ["ingested", "ready"].includes(d.status)
        );
        setRfpDocs(docs);
        setRfpFolderId(Number(folderId));
        if (rfpScopeFolderIds.size || rfpScopeFileKeys.size) {
          const wholeFolder = rfpScopeFolderIds.has(String(folderId));
          // Keep the group collapsed on open (see KB restore note above).
          setChecked((prev) => {
            const next = { ...prev };
            docs.forEach((d) => {
              if (wholeFolder || (d.key && rfpScopeFileKeys.has(d.key))) {
                next[rfpDocUid(d.id)] = true;
              }
            });
            return next;
          });
        }
      } catch (e) {
        if (!cancelled) setRfpError("Failed to load project files.");
      } finally {
        if (!cancelled) setRfpLoading(false);
      }
    };

    setInitializing(true);
    (async () => {
      await Promise.all([fetchKb(), fetchRfp()]);
      if (!cancelled) setInitializing(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, folderId, orgId, initialKbScope, initialRfpScope, selectAllKbWhenEmpty]);

  // Report the computed scope whenever the selection or sources change.
  useEffect(() => {
    const kb_folder_ids = [];
    const kb_file_keys = [];
    kbFolders.forEach((folder) => {
      const docs = folder.documents || [];
      const selected = docs.filter((d) => checked[kbDocUid(folder.id, d.id)]);
      if (docs.length > 0 && selected.length === docs.length) {
        kb_folder_ids.push(folder.id);
      } else {
        selected.forEach((d) => d.key && kb_file_keys.push(d.key));
      }
    });
    kbFiles.forEach((f) => {
      if (checked[kbFileUid(f.id)] && f.key) kb_file_keys.push(f.key);
    });

    const rfp_folder_ids = [];
    const rfp_file_keys = [];
    const selectedRfp = rfpDocs.filter((d) => checked[rfpDocUid(d.id)]);
    if (
      rfpDocs.length > 0 &&
      selectedRfp.length === rfpDocs.length &&
      rfpFolderId != null
    ) {
      rfp_folder_ids.push(rfpFolderId);
    } else {
      selectedRfp.forEach((d) => d.key && rfp_file_keys.push(d.key));
    }

    // Tell the host modal whether anything is selectable yet, so it can allow a
    // whole-KB fallback when both sources are genuinely empty (and only then).
    const hasSources =
      kbFolders.some((f) => (f.documents || []).length > 0) ||
      kbFiles.length > 0 ||
      rfpDocs.length > 0;
    const sourcesReady = !kbLoading && !rfpLoading;

    // Whole-KB selection is semantically identical to an empty KB scope (the
    // backend searches the entire collection when no KB scope is set). Report it
    // so the host can normalise the stored scope to empty and surface a
    // "Complete Knowledge Base" chip instead of "Custom Scope".
    const allKbUids = [
      ...kbFolders.flatMap((f) =>
        (f.documents || []).map((d) => kbDocUid(f.id, d.id))
      ),
      ...kbFiles.map((f) => kbFileUid(f.id)),
    ];
    const kbAllSelected =
      allKbUids.length > 0 && allKbUids.every((u) => checked[u]);

    onChangeRef.current?.({
      kb_folder_ids,
      kb_file_keys,
      rfp_folder_ids,
      rfp_file_keys,
      hasSources,
      sourcesReady,
      kbAllSelected,
    });
  }, [checked, kbFolders, kbFiles, rfpDocs, rfpFolderId, kbLoading, rfpLoading]);

  const toggleExpand = (uid) =>
    setExpanded((prev) => ({ ...prev, [uid]: !prev[uid] }));

  // Accordion for the two top-level groups: opening one collapses the other so
  // the box never grows tall with both trees expanded at once. Folder-level
  // expands inside a group are left untouched.
  const toggleGroup = (groupUid) =>
    setExpanded((prev) => ({
      ...prev,
      "group-kb": groupUid === "group-kb" ? !prev["group-kb"] : false,
      "group-rfp": groupUid === "group-rfp" ? !prev["group-rfp"] : false,
    }));

  const toggleUid = (uid) =>
    setChecked((prev) => ({ ...prev, [uid]: !prev[uid] }));

  // Folder header toggles every document inside it on/off together.
  const toggleKbFolder = (folder) => {
    const docs = folder.documents || [];
    const allOn = docs.length > 0 && docs.every((d) => checked[kbDocUid(folder.id, d.id)]);
    setChecked((prev) => {
      const next = { ...prev };
      docs.forEach((d) => {
        next[kbDocUid(folder.id, d.id)] = !allOn;
      });
      return next;
    });
  };

  // Group header toggles every item belonging to that group together.
  const setGroupAll = (uids, on) =>
    setChecked((prev) => {
      const next = { ...prev };
      uids.forEach((uid) => {
        next[uid] = on;
      });
      return next;
    });

  // Aggregate selection state for the two group-level checkboxes.
  const kbUids = [
    ...kbFolders.flatMap((f) => (f.documents || []).map((d) => kbDocUid(f.id, d.id))),
    ...kbFiles.map((f) => kbFileUid(f.id)),
  ];
  const rfpUids = rfpDocs.map((d) => rfpDocUid(d.id));
  const kbSelectedCount = kbUids.filter((u) => checked[u]).length;
  const rfpSelectedCount = rfpUids.filter((u) => checked[u]).length;
  const kbAllOn = kbUids.length > 0 && kbSelectedCount === kbUids.length;
  const kbSomeOn = kbSelectedCount > 0 && !kbAllOn;
  const rfpAllOn = rfpUids.length > 0 && rfpSelectedCount === rfpUids.length;
  const rfpSomeOn = rfpSelectedCount > 0 && !rfpAllOn;

  const kbGroupOpen = !!expanded["group-kb"];
  const rfpGroupOpen = !!expanded["group-rfp"];

  // Nothing to scope anywhere, after both sources finished loading — the host
  // modal allows a whole-KB fallback in this case (see onChange payload).
  const noSources =
    !kbLoading && !rfpLoading && kbUids.length === 0 && rfpUids.length === 0;

  const renderKbTree = () => {
    if (kbLoading) {
      return (
        <div className="py-6 flex items-center justify-center">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#8B55DD]" />
        </div>
      );
    }
    if (kbError) {
      return (
        <p className="py-4 font-outfit text-[13px] text-[#E21919] text-center">
          {kbError}
        </p>
      );
    }
    if (kbFolders.length === 0 && kbFiles.length === 0) {
      return (
        <p className="py-4 font-outfit text-[13px] text-[#9CA3AF] text-center">
          No files or folders in your knowledge base.
        </p>
      );
    }
    return (
      <div className="space-y-2">
        {kbFolders.map((folder) => {
          const docs = folder.documents || [];
          const total =
            folder.document_count != null ? folder.document_count : docs.length;
          const isEmpty = total === 0;
          const selectedCount = docs.filter(
            (d) => checked[kbDocUid(folder.id, d.id)]
          ).length;
          const allOn = docs.length > 0 && selectedCount === docs.length;
          const someOn = selectedCount > 0 && !allOn;
          const isExpanded = !isEmpty && !!expanded[`folder-${folder.id}`];
          return (
            <div
              key={`folder-${folder.id}`}
              className={`bg-white border rounded-md ${
                isExpanded ? "border-[#8B55DD]" : "border-[#25252580]"
              }`}
            >
              <div className="flex items-center justify-between px-3 h-[35px]">
                <div className="flex items-center gap-3 min-w-0">
                  {isEmpty ? (
                    <span className="opacity-40 cursor-not-allowed">
                      <Checkbox checked={false} />
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => toggleKbFolder(folder)}
                      aria-label={allOn ? "Deselect all" : "Select all"}
                    >
                      <Checkbox checked={allOn} indeterminate={someOn} />
                    </button>
                  )}
                  {isEmpty ? (
                    <div className="flex items-center gap-3 min-w-0">
                      <FolderIcon />
                      <span className="font-outfit text-[14px] text-[#000000B3] truncate">
                        {folder.name}
                      </span>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => toggleExpand(`folder-${folder.id}`)}
                      className="flex items-center gap-3 min-w-0"
                    >
                      <FolderIcon />
                      <span className="font-outfit text-[14px] text-[#000000B3] truncate">
                        {folder.name}
                      </span>
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  {isEmpty ? (
                    <span className="font-outfit text-[12px] text-[#9CA3AF] whitespace-nowrap">
                      No files available
                    </span>
                  ) : (
                    <>
                      {selectedCount > 0 && (
                        <span className="px-2 py-1 rounded-lg bg-[#F2F2F2] text-[#52525B] font-outfit text-[11px] whitespace-nowrap">
                          {selectedCount} Selected
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => toggleExpand(`folder-${folder.id}`)}
                        aria-label={isExpanded ? "Collapse" : "Expand"}
                      >
                        <Chevron up={isExpanded} />
                      </button>
                    </>
                  )}
                </div>
              </div>
              {isExpanded && (
                <div className="border-t border-gray-100 px-3 py-1 max-h-[140px] overflow-y-auto custom-scrollbar">
                  {docs.map((doc) => {
                    const uid = kbDocUid(folder.id, doc.id);
                    return (
                      <div
                        key={uid}
                        onClick={() => toggleUid(uid)}
                        className="flex items-center gap-3 h-[36px] cursor-pointer"
                      >
                        <Checkbox checked={!!checked[uid]} />
                        <span className="font-outfit text-[14px] text-[#000000B3] truncate">
                          {doc.name}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {/* Loose files (not in any folder) */}
        {kbFiles.map((file) => {
          const uid = kbFileUid(file.id);
          return (
            <div
              key={uid}
              onClick={() => toggleUid(uid)}
              className="flex items-center gap-3 bg-white border border-[#25252580] rounded-md px-3 h-[35px] cursor-pointer"
            >
              <Checkbox checked={!!checked[uid]} />
              <span className="font-outfit text-[14px] text-[#000000B3] truncate">
                {file.name}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  const renderRfpTree = () => {
    if (rfpLoading) {
      return (
        <div className="py-6 flex items-center justify-center">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#8B55DD]" />
        </div>
      );
    }
    if (rfpError) {
      return (
        <p className="py-4 font-outfit text-[13px] text-[#E21919] text-center">
          {rfpError}
        </p>
      );
    }
    if (rfpDocs.length === 0) {
      return (
        <p className="py-4 font-outfit text-[13px] text-[#9CA3AF] text-center">
          No files in this project.
        </p>
      );
    }
    return (
      <div className="space-y-2">
        {rfpDocs.map((doc) => {
          const uid = rfpDocUid(doc.id);
          return (
            <div
              key={uid}
              onClick={() => toggleUid(uid)}
              className="flex items-center gap-3 bg-white border border-[#25252580] rounded-md px-3 h-[35px] cursor-pointer"
            >
              <Checkbox checked={!!checked[uid]} />
              <span className="font-outfit text-[14px] text-[#000000B3] truncate">
                {doc.name}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div>
      <p className="font-outfit text-[14px] font-medium text-[#3F3F46] mb-2">
        Knowledge Source Scope <span className="text-[#374151]">*</span>
      </p>
      {initializing ? (
        <div className="bg-[#F7F7F7] border border-gray-200 rounded-xl px-3">
          {/* Skeleton mirroring the two collapsible group rows, so the picker
              keeps its shape while the sources + initial selection load. */}
          {[0, 1].map((row) => (
            <div
              key={row}
              className={`py-1.5 ${row === 0 ? "border-b border-[#DBDBDB]" : ""}`}
            >
              <div className="flex items-center justify-between px-1 py-2 animate-pulse">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="h-4 w-4 rounded bg-gray-200" />
                  <span
                    className="h-3.5 rounded bg-gray-200"
                    style={{ width: row === 0 ? 190 : 150 }}
                  />
                </div>
                <span className="h-4 w-4 rounded bg-gray-200" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-[#F7F7F7] border border-gray-200 rounded-xl px-3 max-h-[60vh] overflow-y-auto custom-scrollbar">
          <Group
            label="Knowledge Base Files & Folder"
            open={kbGroupOpen}
            onToggleOpen={() => toggleGroup("group-kb")}
            allOn={kbAllOn}
            someOn={kbSomeOn}
            selectedCount={kbSelectedCount}
            onToggleAll={() => setGroupAll(kbUids, !kbAllOn)}
            showDivider
          >
            {renderKbTree()}
          </Group>
          <Group
            label="Project Files & Folder"
            open={rfpGroupOpen}
            onToggleOpen={() => toggleGroup("group-rfp")}
            allOn={rfpAllOn}
            someOn={rfpSomeOn}
            selectedCount={rfpSelectedCount}
            onToggleAll={() => setGroupAll(rfpUids, !rfpAllOn)}
          >
            {renderRfpTree()}
          </Group>
        </div>
      )}
      <div className="flex items-start gap-2 mt-3 text-[#6B7280]">
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          className="mt-0.5 flex-shrink-0"
        >
          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
          <path
            d="M12 11v5"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
          <circle cx="12" cy="7.75" r="1" fill="currentColor" />
        </svg>
        <p className="font-outfit text-[13px] text-[#52525B]">
          {noSources
            ? "No documents available to scope — the complete knowledge base will be used."
            : "Restricting the scope can improve relevance and alignment with the proposal context."}
        </p>
      </div>
    </div>
  );
};

export default KnowledgeSourceScope;
